/**
 * Admin authentication helper.
 *
 * Per the salon owner's request, an admin stays logged in until they
 * explicitly click "Déconnexion". To achieve that:
 *   - The session token is stored in **localStorage** (survives tab close,
 *     browser restart, phone reboot — only cleared by explicit logout).
 *   - The backend session in MongoDB has no TTL.
 *
 * For backwards compatibility we also read any token previously written to
 * sessionStorage and migrate it to localStorage on first call.
 */
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const STORAGE_KEY = 'hilanta_admin_token';

let _onUnauthorized = null;

export function setUnauthorizedHandler(handler) {
  _onUnauthorized = handler;
}

export function getAdminToken() {
  // localStorage first (new behaviour), fallback to sessionStorage and migrate.
  const fromLocal = localStorage.getItem(STORAGE_KEY);
  if (fromLocal) return fromLocal;
  const legacy = sessionStorage.getItem(STORAGE_KEY);
  if (legacy) {
    localStorage.setItem(STORAGE_KEY, legacy);
    sessionStorage.removeItem(STORAGE_KEY);
    return legacy;
  }
  return '';
}

export function setAdminToken(token) {
  if (token) {
    localStorage.setItem(STORAGE_KEY, token);
  } else {
    localStorage.removeItem(STORAGE_KEY);
    sessionStorage.removeItem(STORAGE_KEY);
  }
}

export function clearAdminToken() {
  localStorage.removeItem(STORAGE_KEY);
  sessionStorage.removeItem(STORAGE_KEY);
}

/**
 * Posts the admin password to the backend and stores the returned session token.
 * Throws on failure (network error or 401). Caller is expected to surface a toast.
 */
export async function loginAdmin(password, pageType = 'admin') {
  const res = await axios.post(`${API}/auth/admin-login`, {
    password,
    page_type: pageType,
  });
  const token = res.data?.session_token;
  if (!token) throw new Error('Token manquant dans la réponse');
  setAdminToken(token);
  return token;
}

export async function logoutAdmin() {
  const token = getAdminToken();
  clearAdminToken();
  if (!token) return;
  try {
    await axios.post(`${API}/auth/admin-logout`, null, {
      headers: { 'X-Admin-Token': token },
    });
  } catch (err) {
    // Best-effort: the session is already cleared locally. Log for debugging
    // but never surface to the user — logging out should never fail visibly.
    console.warn('[adminAuth] logout request failed', err?.message || err);
  }
}

/** Quick server-side check that the stored token is still valid. */
export async function checkAdminSession() {
  const token = getAdminToken();
  if (!token) return false;
  try {
    await axios.get(`${API}/auth/admin-check`, {
      headers: { 'X-Admin-Token': token },
    });
    return true;
  } catch (err) {
    // Token is rejected or network error — clear it locally so the UI shows login again.
    if (err?.response?.status !== 401) {
      console.warn('[adminAuth] session check error', err?.message || err);
    }
    clearAdminToken();
    return false;
  }
}

// ---- Axios interceptors -----------------------------------------------------

// Inject the admin token on every outbound request (no-op if missing)
axios.interceptors.request.use((config) => {
  const token = getAdminToken();
  if (token) {
    config.headers = config.headers || {};
    if (!config.headers['X-Admin-Token']) {
      config.headers['X-Admin-Token'] = token;
    }
  }
  return config;
});

// On a REAL 401 from the server, log the user out in the SPA.
// Important: we explicitly require `error.response` to exist. A network error
// (timeout, offline, DNS hiccup, mobile signal drop) has NO `error.response`,
// only `error.request`. Those must NOT clear the token — otherwise the user
// gets logged out every time their 4G connection flickers for half a second.
axios.interceptors.response.use(
  (response) => response,
  (error) => {
    // Only act when the SERVER actually responded with 401.
    if (error && error.response && error.response.status === 401) {
      const url = error.config?.url || '';
      if (url.includes('/api/') && getAdminToken()) {
        clearAdminToken();
        if (typeof _onUnauthorized === 'function') {
          try {
            _onUnauthorized();
          } catch (err) {
            console.warn('[adminAuth] onUnauthorized handler threw', err?.message || err);
          }
        }
      }
    }
    return Promise.reject(error);
  }
);
